import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ClipboardList,
  Mail,
  Palette,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { ROLE_LABELS, statusLabel } from "../../lib/labels";

interface CompanyDetailResponse {
  company: {
    id: string;
    slug: string;
    name: string;
    phone: string | null;
    email: string | null;
    address: string | null;
    website: string | null;
    primary_color: string;
    greeting_style: "sie" | "du";
    tracking_page_url: string | null;
    email_signature: string | null;
    email_sender_name: string | null;
    notification_email: string | null;
    notfall_notification: boolean;
    active: boolean;
    services: unknown[];
  };
  users: { id: string; name: string; role: "admin" | "staff"; created_at: string }[];
  recentAnfragen: {
    id: string;
    reference_number: string;
    status: string;
    customer_vorname: string;
    customer_nachname: string | null;
    created_at: string;
  }[];
}

export function CompanyDetail() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["company", slug],
    queryFn: () => api<CompanyDetailResponse>(`/master/companies/${slug}`),
  });

  const [form, setForm] = useState<Partial<CompanyDetailResponse["company"]>>({});
  useEffect(() => {
    if (data) setForm(data.company);
  }, [data]);

  function field<K extends keyof CompanyDetailResponse["company"]>(
    key: K,
    value: CompanyDetailResponse["company"][K],
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const save = useMutation({
    mutationFn: () =>
      api(`/master/companies/${slug}`, {
        method: "PUT",
        body: {
          name: form.name,
          phone: form.phone,
          email: form.email,
          address: form.address,
          website: form.website,
          primaryColor: form.primary_color,
          greetingStyle: form.greeting_style,
          trackingPageUrl: form.tracking_page_url,
          emailSignature: form.email_signature,
          emailSenderName: form.email_sender_name,
          notificationEmail: form.notification_email,
          notfallNotification: form.notfall_notification,
          active: form.active,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["company", slug] }),
  });

  const deleteCo = useMutation({
    mutationFn: () => api(`/master/companies/${slug}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      navigate("/master");
    },
  });

  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "staff" as "staff" | "admin",
  });
  const [userError, setUserError] = useState<string | null>(null);

  const addUser = useMutation({
    mutationFn: () =>
      api(`/master/companies/${slug}/users`, {
        method: "POST",
        body: newUser,
      }),
    onSuccess: () => {
      setNewUser({ name: "", email: "", role: "staff" });
      setUserError(null);
      qc.invalidateQueries({ queryKey: ["company", slug] });
    },
    onError: (err) =>
      setUserError(err instanceof ApiError ? err.message : "Fehler"),
  });

  const removeUser = useMutation({
    mutationFn: (userId: string) =>
      api(`/master/companies/${slug}/users/${userId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["company", slug] }),
  });

  if (isLoading) return <p className="text-slate-500">Lädt…</p>;
  if (error || !data)
    return (
      <p>
        Nicht gefunden.{" "}
        <Link to="/master" className="text-brand hover:underline">
          ← zurück
        </Link>
      </p>
    );

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <Link
        to="/master"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Unternehmen
      </Link>

      <header className="flex flex-wrap items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-sm font-semibold text-slate-700">
          {initials(data.company.name)}
        </div>
        <div>
          <h1 className="page-title">{data.company.name}</h1>
          <p className="text-sm text-slate-500">
            <span className="font-mono">/{data.company.slug}</span>
            <span className="mx-2 text-slate-300">·</span>
            {data.company.active ? (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <Check className="h-3 w-3" /> Aktiv
              </span>
            ) : (
              <span className="text-slate-500">Inaktiv</span>
            )}
          </p>
        </div>
        <Link
          to={`/master/companies/${slug}/board`}
          className="btn-secondary ml-auto"
        >
          Anfragen ansehen <ArrowRight className="h-4 w-4" />
        </Link>
      </header>

      <section className="card">
        <h2 className="section-title mb-4">
          <Building2 className="h-3.5 w-3.5 text-slate-400" /> Profil
        </h2>
        <div className="space-y-3">
          <Row label="Firmenname">
            <input
              className="input"
              value={form.name ?? ""}
              onChange={(e) => field("name", e.target.value)}
            />
          </Row>
          <Row label="Telefon">
            <input
              className="input"
              value={form.phone ?? ""}
              onChange={(e) => field("phone", e.target.value)}
            />
          </Row>
          <Row label="E-Mail">
            <input
              className="input"
              value={form.email ?? ""}
              onChange={(e) => field("email", e.target.value)}
            />
          </Row>
          <Row label="Adresse">
            <input
              className="input"
              value={form.address ?? ""}
              onChange={(e) => field("address", e.target.value)}
            />
          </Row>
          <Row label="Website">
            <input
              className="input"
              value={form.website ?? ""}
              onChange={(e) => field("website", e.target.value)}
            />
          </Row>
          <Row label="Tracking-Seite">
            <input
              className="input"
              placeholder="https://kunde.de/kontakt"
              value={form.tracking_page_url ?? ""}
              onChange={(e) => field("tracking_page_url", e.target.value)}
            />
          </Row>
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Palette className="h-3.5 w-3.5 text-slate-400" /> Branding
        </h2>
        <div className="space-y-3">
          <Row label="Primärfarbe">
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200"
                value={form.primary_color ?? "#1a1a2e"}
                onChange={(e) => field("primary_color", e.target.value)}
              />
              <span className="font-mono text-sm text-slate-600">
                {form.primary_color}
              </span>
            </div>
          </Row>
          <Row label="Anrede">
            <select
              className="input"
              value={form.greeting_style ?? "sie"}
              onChange={(e) =>
                field("greeting_style", e.target.value as "sie" | "du")
              }
            >
              <option value="sie">Sie</option>
              <option value="du">Du</option>
            </select>
          </Row>
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Mail className="h-3.5 w-3.5 text-slate-400" /> E-Mail
        </h2>
        <div className="space-y-3">
          <Row label="Absendername">
            <input
              className="input"
              value={form.email_sender_name ?? ""}
              onChange={(e) => field("email_sender_name", e.target.value)}
            />
          </Row>
          <Row label="Benachrichtigung an">
            <input
              className="input"
              value={form.notification_email ?? ""}
              onChange={(e) => field("notification_email", e.target.value)}
            />
          </Row>
          <Row label="Signatur">
            <textarea
              className="input h-24"
              value={form.email_signature ?? ""}
              onChange={(e) => field("email_signature", e.target.value)}
            />
          </Row>
          <Row label="Notfall-Benachrichtigung">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
              checked={form.notfall_notification ?? true}
              onChange={(e) => field("notfall_notification", e.target.checked)}
            />
          </Row>
          <Row label="Aktiv">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
              checked={form.active ?? true}
              onChange={(e) => field("active", e.target.checked)}
            />
          </Row>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <button
            className="btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            Speichern
          </button>
          {save.isSuccess && (
            <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
              <Check className="h-4 w-4" /> Gespeichert
            </span>
          )}
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Users className="h-3.5 w-3.5 text-slate-400" /> Team
          <span className="ml-1 text-slate-400">({data.users.length})</span>
        </h2>
        <ul className="divide-y divide-slate-100">
          {data.users.map((u) => (
            <li
              key={u.id}
              className="flex items-center justify-between py-3 text-sm"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                  {initials(u.name)}
                </div>
                <div>
                  <div className="font-medium text-slate-900">{u.name}</div>
                  <div className="text-xs text-slate-500">
                    {u.role === "admin" ? "Geschäftsführer" : "Bürokraft"} · seit{" "}
                    {new Date(u.created_at).toLocaleDateString("de-DE")}
                  </div>
                </div>
              </div>
              <button
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                onClick={() => {
                  if (confirm(`${u.name} wirklich entfernen?`))
                    removeUser.mutate(u.id);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" /> Entfernen
              </button>
            </li>
          ))}
        </ul>

        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <UserPlus className="h-3.5 w-3.5" /> Neuer Benutzer
          </h3>
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              className="input"
              placeholder="Name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
            />
            <input
              className="input"
              type="email"
              placeholder="E-Mail"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
            <select
              className="input"
              value={newUser.role}
              onChange={(e) =>
                setNewUser({ ...newUser, role: e.target.value as "admin" | "staff" })
              }
            >
              <option value="staff">Bürokraft</option>
              <option value="admin">Geschäftsführer</option>
            </select>
          </div>
          {userError && (
            <p className="mt-2 inline-flex items-start gap-2 text-xs text-red-600">
              <AlertCircle className="mt-px h-3.5 w-3.5" />
              {userError}
            </p>
          )}
          <button
            className="btn-primary btn-sm mt-3"
            disabled={
              !newUser.name ||
              !newUser.email ||
              addUser.isPending
            }
            onClick={() => addUser.mutate()}
          >
            Hinzufügen
          </button>
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <ClipboardList className="h-3.5 w-3.5 text-slate-400" /> Letzte Anfragen
        </h2>
        {data.recentAnfragen.length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine Anfragen.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {data.recentAnfragen.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between py-2"
              >
                <div>
                  <span className="font-medium text-slate-900">
                    {a.customer_vorname} {a.customer_nachname ?? ""}
                  </span>
                  <span className="ml-2 font-mono text-xs text-slate-500">
                    {a.reference_number}
                  </span>
                </div>
                <span className="pill bg-slate-100 text-slate-600">
                  {statusLabel(a.status)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card border-red-200 bg-red-50">
        <h2 className="section-title mb-3 text-red-700">
          <AlertTriangle className="h-3.5 w-3.5" /> Gefahrenbereich
        </h2>
        <p className="mb-4 text-sm text-red-700">
          Das Unternehmen und <strong>alle Daten</strong> (Anfragen, Fotos,
          Dokumente, Benutzer) werden unwiderruflich gelöscht. Diese Aktion
          erfüllt eine DSGVO-Löschanfrage.
        </p>
        <button
          className="btn-danger btn-sm"
          onClick={() => {
            const ok = prompt(
              `Zum Bestätigen den Firmen-Slug "${data.company.slug}" eintippen:`,
            );
            if (ok === data.company.slug) deleteCo.mutate();
          }}
          disabled={deleteCo.isPending}
        >
          <Trash2 className="h-3.5 w-3.5" /> Unternehmen löschen
        </button>
      </section>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-2 sm:grid-cols-[180px_1fr] sm:items-center">
      <span className="text-sm text-slate-600">{label}</span>
      <div>{children}</div>
    </label>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
